import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useOcrInputStore } from '../store/ocrInputStore';
import { fetchProcessedImage, processImageForData } from '../api/dataInputApi';
import { OcrProcessingResult, ExtractedData } from '../types/dataInputTypes';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { OCR_IMAGE_PROCESSED } from '../../../modules/analytics/events';

interface ProcessVariables {
  imageFile: File;
  templateId: string;
  signal: AbortSignal;
  assetId: string;
  logbookTemplateId: string;
}

const processOcrData = async (variables: ProcessVariables): Promise<OcrProcessingResult> => {
  const { imageFile, templateId, assetId, logbookTemplateId, signal } = variables;
  const { data } = await processImageForData(imageFile, templateId, assetId, logbookTemplateId, signal);

  if (!data?.imageId)
    throw new Error('Failed to process image or did not receive an image ID.');
  const imageResponse = await fetchProcessedImage(data, signal);
  return { extractedData: data.extractedData, processedImageUrl: imageResponse.imageUrl, dataInputId: data.dataInputId };
};

export const useOcrDataProcessor = (originalImageUrl: string) => {
  const { setProcessingSuccess, selectedTemplate, selectedAsset } = useOcrInputStore();

  const { mutate, data, error, isPending, isSuccess, isError, reset } = useMutation<
    OcrProcessingResult,
    Error,
    ProcessVariables
  >({
    mutationFn: processOcrData,
    onSuccess: data => {
      trackEvent(OCR_IMAGE_PROCESSED, { dataInputId: data.dataInputId || '', templateId: selectedTemplate?.logbookTemplateId || '', assetId: selectedAsset?.assetId || '' });
      toast.success('Image processed and data extracted successfully!');

      setProcessingSuccess({
        extractedData: data.extractedData as unknown as ExtractedData[],
        processedImageUrl: data.processedImageUrl,
        originalImageUrl,
        dataInputId: data.dataInputId
      });
    },
    onError: error => {
      toast.error(error.message || 'An unknown error occurred during processing.');
    },
    retry: 1,
  });

  return {
    processOcr: mutate,
    processedData: data,
    processingError: error,
    isProcessing: isPending,
    isSuccess,
    isError,
    resetMutation: reset,
  };
};
